import { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from '@shared-ui/components';
import { questionApi } from '../../api/questionApi';
import type { CurationChanges, SimStatus } from '../../api/simulationsApi';
import type { Module } from '../../types';
import { BoardGradeEditor } from '../../components/BoardGradeEditor';
import './CurationEditor.css';

/** The fields this edits, as any catalogue entry carries them. */
export interface Curatable {
  id: string;
  title: string;
  subject: string;
  status: SimStatus;
  moduleIds: string[];
  boardGrades?: string[];
  boardGradesCuratedAt?: string;
}

/**
 * What admin curates on a catalogue entry — a simulation or a guided
 * experiment — and all it curates: publish status, the chapters it attaches to
 * (the `moduleIds` that drive Concept-Lab matching), and who it is for.
 * Everything else comes from the repo catalogue.
 *
 * The picker offers TOPICS only, though the field stores module ids at any
 * level. A topic is the grain a simulation actually illustrates — "Wave Optics",
 * not the whole chapter that contains it — and offering all three levels in one
 * flat list mostly produced associations that were too coarse to be useful for
 * matching. Existing chips at chapter or module level are left alone and can
 * still be removed; only what you can ADD is narrowed.
 *
 * It owns its dialog rather than being dropped inside someone else's: the Save
 * belongs in that dialog's footer, and only this component knows whether there
 * is anything to save. A hand-rolled button in the body put the one action of
 * the dialog in the middle of the scroll, at a size and with a label of its
 * own — the standard footer is what every other editor here uses.
 */
export function CurationEditor<T extends Curatable>({ entry, isOpen, onClose, save: send, onSaved }: {
  entry: T;
  isOpen: boolean;
  onClose: () => void;
  /** Sends only what changed, and returns the entry as stored. */
  save: (id: string, changes: CurationChanges) => Promise<T>;
  onSaved: (entry: T) => void;
}) {
  const [modules, setModules] = useState<Module[]>([]);
  const [moduleIds, setModuleIds] = useState<string[]>(entry.moduleIds ?? []);
  const [status, setStatus] = useState<SimStatus>(entry.status);
  const [boardGrades, setBoardGrades] = useState<string[]>(entry.boardGrades ?? []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Subject's module catalog — the pool to associate from (names for the chips).
  useEffect(() => {
    questionApi
      .getModules({ subject: entry.subject, full: true })
      .then((r) => setModules(r.modules ?? []))
      .catch(() => setModules([]));
  }, [entry.subject]);

  const nameOf = useMemo(() => {
    const m = new Map(modules.map((x) => [x.id, x]));
    return (id: string) => {
      const mod = m.get(id);
      return mod ? `${mod.displayName}${mod.moduleLevel ? ` · ${mod.moduleLevel}` : ''}` : id;
    };
  }, [modules]);

  const topics = modules.filter((m) => m.moduleLevel === 'topic');
  const available = topics
    .filter((m) => !moduleIds.includes(m.id))
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  // "None left to add" and "this subject has no topics" are different problems
  // — the second means the taxonomy needs work, not this entry.
  const emptyReason = topics.length === 0 ? 'No topics in this subject' : 'All topics added';

  const same = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
  const dirty =
    status !== entry.status ||
    !same(moduleIds, entry.moduleIds ?? []) ||
    !same(boardGrades, entry.boardGrades ?? []);

  const add = (id: string) => { if (id && !moduleIds.includes(id)) setModuleIds([...moduleIds, id]); };
  const remove = (id: string) => setModuleIds(moduleIds.filter((x) => x !== id));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const updated = await send(entry.id, {
        ...(status !== entry.status ? { status } : {}),
        ...(!same(moduleIds, entry.moduleIds ?? []) ? { moduleIds } : {}),
        ...(!same(boardGrades, entry.boardGrades ?? []) ? { boardGrades } : {}),
      });
      onSaved(updated);
      // Saved is what closing says. A dialog that stays open with a green
      // "Saved" in it leaves the reader to work out whether anything else is
      // expected of them.
      onClose();
    } catch (e) {
      setMsg((e as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit — ${entry.title}`}
      size="lg"
      footer={
        <>
          {msg && <span className="sim-assoc-msg err">{msg}</span>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving} disabled={!dirty || saving}>Save</Button>
        </>
      }
    >
      {/* No card inside the dialog: it already has a frame, a title and its own
          padding, and a second border around the same fields was a box in a box
          that cost a line of height and said nothing. */}
      <p className="sim-assoc-hint">
        Modules this sim illustrates — drives which questions surface it in Concept Lab.
      </p>

      <label className="sim-assoc-row">
        <span className="sim-assoc-label">Status</span>
        <select value={status} onChange={(e) => setStatus(e.target.value as SimStatus)}>
          <option value="live">Live</option>
          <option value="draft">Draft</option>
        </select>
      </label>

      <BoardGradeEditor
        value={boardGrades}
        onChange={setBoardGrades}
        help={
          entry.boardGradesCuratedAt
            ? 'Who this is for. Curated.'
            : 'Who this is for. Not yet reviewed — these were proposed by the seed from the chapters this attaches to, which is an upper bound: a chapter taught in three grades says nothing about which of them this simulation suits. Saving marks it curated.'
        }
      />

      <div className="sim-assoc-row">
        <span className="sim-assoc-label">Modules</span>
        <div className="sim-assoc-chips">
          {moduleIds.length === 0 && <span className="sim-assoc-empty">None yet — matches by subject + topic only.</span>}
          {moduleIds.map((mid) => (
            <span key={mid} className="sim-assoc-chip">
              {nameOf(mid)}
              <button type="button" className="sim-assoc-x" onClick={() => remove(mid)} aria-label="Remove">✕</button>
            </span>
          ))}
        </div>
      </div>

      <div className="sim-assoc-row">
        <span className="sim-assoc-label">Add topic</span>
        <select value="" onChange={(e) => add(e.target.value)} disabled={available.length === 0}>
          <option value="">{available.length ? 'Select a topic…' : emptyReason}</option>
          {/* No ` · topic` suffix here — every option is one, so it would be
              noise. The chips keep it, where the levels can be mixed. */}
          {available.map((m) => (
            <option key={m.id} value={m.id}>{m.displayName}</option>
          ))}
        </select>
      </div>
    </Modal>
  );
}
